import React, { useState, useEffect, useMemo, useCallback } from 'react';

const DIAS_SEMANA = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes'];

const crearEstructuraVacia = () =>
  Array.from({ length: 5 }, (_, i) => ({
    semana: i + 1,
    dias: DIAS_SEMANA.reduce((acc, dia) => {
      acc[dia] = { sabor: '', olor: '', color: '', materia: '', cloroDato: '', cloro: '', phDato: '', ph: '' };
      return acc;
    }, {})
  }));

// Combina múltiples registros del mes en una sola matriz de 5 semanas
const combinarSemanasAgua = (registrosMes = []) => {
  const base = crearEstructuraVacia();
  let muestraDetectada = '';
  let mantenimientosDetectados = [];

  // Recorremos del más antiguo al más reciente
  [...registrosMes].reverse().forEach((reg) => {
    let resp = reg?.respuestas || {};
    if (typeof resp === 'string') {
      try { resp = JSON.parse(resp); } catch { resp = {}; }
    }

    const mTomada = String(resp.muestra_tomada_de || resp.muestraTomadaDe || '').trim();
    if (mTomada) muestraDetectada = mTomada;

    if (Array.isArray(resp.mantenimientos) && resp.mantenimientos.length > 0) {
      mantenimientosDetectados = resp.mantenimientos;
    }

    const listaSemanas = resp.mediciones || resp.semanas || (Array.isArray(resp) ? resp : []);
    if (!Array.isArray(listaSemanas)) return;

    listaSemanas.forEach((semItem, idx) => {
      const numSem = Number(semItem?.semana || idx + 1);
      const targetSem = base[numSem - 1];
      if (!targetSem || !semItem?.dias) return;

      DIAS_SEMANA.forEach((dia) => {
        const datosDia = semItem.dias[dia] || (dia === 'Miércoles' ? semItem.dias['Miercoles'] : null);
        if (!datosDia) return;
        Object.entries(datosDia).forEach(([campo, valor]) => {
          if (valor !== undefined && valor !== null && String(valor).trim() !== '') {
            targetSem.dias[dia][campo] = String(valor).trim();
          }
        });
      });
    });
  });

  return {
    mediciones: base,
    muestra_tomada_de: muestraDetectada,
    mantenimientos: mantenimientosDetectados
  };
};

export default function FormatoAguaPotable({ usuario, onBack }) {
  const [sedesDisponibles, setSedesDisponibles] = useState([]);
  const [cargandoPlanilla, setCargandoPlanilla] = useState(false);
  const [guardando, setGuardando] = useState(false);

  const permisosABAC = useMemo(() => {
    try {
      const parsed = typeof usuario?.permisos_avanzados === 'string'
        ? JSON.parse(usuario.permisos_avanzados)
        : (usuario?.permisos_avanzados || {});
      return parsed.micros || parsed;
    } catch (e) { return {}; }
  }, [usuario]);

  const nombreInspector = useMemo(() => {
    return (
      `${usuario?.nombres || ''} ${usuario?.apellidos || ''}`.trim() ||
      usuario?.nombre_completo ||
      usuario?.terc_nom ||
      usuario?.nombre ||
      'Inspector SST'
    );
  }, [usuario]);

  const [formData, setFormData] = useState({
    pdv: '',
    fecha: new Date().toISOString().split('T')[0],
    inspector: nombreInspector,
    muestra_tomada_de: ''
  });

  // Calculamos automáticamente la semana sugerida del mes actual (1 a 5), pero el usuario puede ver cualquiera
  const semanaDelMesActual = Math.min(5, Math.ceil(new Date().getDate() / 7));
  const [mesSeleccionado, setMesSeleccionado] = useState(String(new Date().getMonth() + 1).padStart(2, '0'));
  const [anioSeleccionado, setAnioSeleccionado] = useState(String(new Date().getFullYear()));
  const [semanaActiva, setSemanaActiva] = useState(semanaDelMesActual);

  const [mediciones, setMediciones] = useState(crearEstructuraVacia);
  const [mantenimientos, setMantenimientos] = useState([]);
  const [mensaje, setMensaje] = useState({ text: '', type: '' });

  // 1. Cargar sedes disponibles según permisos del usuario
  useEffect(() => {
    const fetchSedes = async () => {
      try {
        const apiUrl = process.env.REACT_APP_API_URL;
        const res = await fetch(`${apiUrl}/api/sedes`);
        const data = await res.json();
        const listaSedes = Array.isArray(data) ? data : (data.sedes || data.data || []);

        if (listaSedes.length > 0) {
          const cargoUpper = String(usuario?.cargo || '').toUpperCase();
          const esAdminGlobal = cargoUpper.includes('SUPER');

          const configPermiso = permisosABAC['Otros Formatos SST'] || {};
          const alcance = configPermiso.alcance || 'PROPIA';
          const sedesEspecificas = configPermiso.sedes_ids || [];

          let filtradas = listaSedes;
          if (!esAdminGlobal && alcance !== 'GLOBAL') {
            if (alcance === 'ESPECIFICA' && sedesEspecificas.length > 0) {
              filtradas = listaSedes.filter(s =>
                sedesEspecificas.includes(String(s.id)) || sedesEspecificas.includes(String(s.codigo_bodega))
              );
            } else {
              const FiltradasPorCodigo = listaSedes.filter(s =>
                String(s.codigo_bodega) === String(usuario?.codigo_bodega || usuario?.sede_id) ||
                String(s.nombre || '').trim().toUpperCase() === String(usuario?.centroTrabajo || usuario?.sede || '').trim().toUpperCase()
              );
              if (FiltradasPorCodigo.length > 0) filtradas = FiltradasPorCodigo;
            }
          }

          setSedesDisponibles(filtradas);
          if (filtradas.length === 1) {
            setFormData(prev => ({ ...prev, pdv: String(filtradas[0].nombre || '').toUpperCase() }));
          } else if (!formData.pdv && usuario?.centroTrabajo) {
            const coincide = filtradas.find(
              s => String(s.nombre || '').trim().toUpperCase() === String(usuario.centroTrabajo).trim().toUpperCase()
            );
            if (coincide) {
              setFormData(prev => ({ ...prev, pdv: coincide.nombre.toUpperCase() }));
            }
          }
        }
      } catch (e) {
        console.error('Error al cargar sedes:', e);
      }
    };
    fetchSedes();
  }, [usuario, permisosABAC, formData.pdv]);

  // 2. Cargar automáticamente los datos ya diligenciados en el mes (Semana 1, Semana 2, etc.) cuando cambia Sede o Mes
  const cargarAvanceMensualSede = useCallback(async (pdvActual, anioActual, mesActual) => {
    if (!pdvActual) return;
    setCargandoPlanilla(true);
    try {
      const apiUrl = process.env.REACT_APP_API_URL ;
      const token = localStorage.getItem('token');
      const mesFiltro = `${anioActual}-${mesActual}`;

      const res = await fetch(
        `${apiUrl}/api/sst/formularios/historial/AGUA_POTABLE_SST?sede=${encodeURIComponent(pdvActual)}&mes=${encodeURIComponent(mesFiltro)}`,
        {
          headers: {
            'Content-Type': 'application/json',
            ...(token && { Authorization: `Bearer ${token}` })
          }
        }
      );

      if (res.ok) {
        const data = await res.json();
        const todos = data.data || data.registros || [];

        // Filtramos los que coincidan con la sede y el mes seleccionado
        const registrosDeSedeMes = todos.filter((r) => {
          const sedeReg = String(r.pdv_centro || r.sede || '').trim().toUpperCase();
          const fechaReg = String(r.fecha || '').slice(0, 7);
          return sedeReg === String(pdvActual).trim().toUpperCase() && fechaReg === mesFiltro;
        });

        if (registrosDeSedeMes.length > 0) {
          const consolidado = combinarSemanasAgua(registrosDeSedeMes);
          setMediciones(consolidado.mediciones);
          setMantenimientos(consolidado.mantenimientos);
          setFormData(prev => ({
            ...prev,
            muestra_tomada_de: consolidado.muestra_tomada_de || prev.muestra_tomada_de
          }));
        } else {
          setMediciones(crearEstructuraVacia());
          setMantenimientos([]);
          setFormData(prev => ({ ...prev, muestra_tomada_de: '' }));
        }
      }
    } catch (err) {
      console.error('Error precargando planilla mensual de agua potable:', err);
    } finally {
      setCargandoPlanilla(false);
    }
  }, []);

  useEffect(() => {
    if (formData.pdv && anioSeleccionado && mesSeleccionado) {
      cargarAvanceMensualSede(formData.pdv, anioSeleccionado, mesSeleccionado);
    }
  }, [formData.pdv, anioSeleccionado, mesSeleccionado, cargarAvanceMensualSede]);

  // Verifica si una semana ya tiene al menos un dato diligenciado (para mostrar badge visual en los botones S1..S5)
  const semanaTieneDatos = (numSem) => {
    const sem = mediciones[numSem - 1];
    if (!sem || !sem.dias) return false;
    return DIAS_SEMANA.some(dia =>
      Object.values(sem.dias[dia] || {}).some(val => String(val || '').trim() !== '')
    );
  };

  // Auto-calcula C / NC cuando el usuario escribe el valor numérico de Cloro (0.3 a 2.0) o pH (6.5 a 9.0)
  const handleMedicionChange = (dia, campo, valor) => {
    setMediciones(prev => {
      const copia = prev.map((sem, idx) => {
        if (idx !== semanaActiva - 1) return sem;
        const nuevoDia = { ...sem.dias[dia], [campo]: valor };

        if (campo === 'cloroDato') {
          const num = parseFloat(String(valor).replace(',', '.'));
          if (!isNaN(num)) {
            nuevoDia.cloro = num >= 0.3 && num <= 2.0 ? 'C' : 'NC';
          }
        }
        if (campo === 'phDato') {
          const num = parseFloat(String(valor).replace(',', '.'));
          if (!isNaN(num)) {
            nuevoDia.ph = num >= 6.5 && num <= 9.0 ? 'C' : 'NC';
          }
        }

        return {
          ...sem,
          dias: {
            ...sem.dias,
            [dia]: nuevoDia
          }
        };
      });
      return copia;
    });
  };

  const agregarMantenimiento = () => {
    setMantenimientos(prev => [...prev, { elemento: '', fecha: '', descripcion: '', observaciones: '' }]);
  };

  const guardarRegistro = async () => {
    if (!formData.pdv) {
      setMensaje({ text: '⚠️ Por favor selecciona la Sede (PDV).', type: 'warning' });
      return;
    }

    setGuardando(true);
    try {
      const token = localStorage.getItem('token');
      const apiUrl = process.env.REACT_APP_API_URL;

      const payload = {
        tipo_formulario: 'AGUA_POTABLE_SST',
        datosGenerales: {
          pdv: formData.pdv,
          fecha: `${anioSeleccionado}-${mesSeleccionado}-01`,
          evaluador: formData.inspector || nombreInspector
        },
        respuestas: {
          muestra_tomada_de: formData.muestra_tomada_de || 'Acueducto / Tanque',
          muestraTomadaDe: formData.muestra_tomada_de || 'Acueducto / Tanque',
          mediciones: mediciones,
          semanas: mediciones,
          mantenimientos: mantenimientos
        },
        observaciones: {},
        puntaje_global: 100,
        evidencias: {}
      };

      const res = await fetch(`${apiUrl}/api/sst/guardar`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` })
        },
        body: JSON.stringify(payload)
      });

      const dataRes = await res.json();
      if (res.ok && dataRes.success) {
        setMensaje({
          text: `✅ Avance de la Semana ${semanaActiva} (${formData.pdv}) guardado y sincronizado con las demás semanas del mes.`,
          type: 'success'
        });
        setTimeout(() => setMensaje({ text: '', type: '' }), 4000);
      } else {
        setMensaje({ text: dataRes.message || 'Error al guardar.', type: 'error' });
      }
    } catch (error) {
      setMensaje({ text: 'Error de conexión con el servidor.', type: 'error' });
    } finally {
      setGuardando(false);
    }
  };

  const semanaActualData = mediciones[semanaActiva - 1] || crearEstructuraVacia()[0];
  const inputClass = 'w-full h-10 px-3 bg-white/80 border-transparent focus:border-sky-400 rounded-xl text-xs font-bold text-slate-800 shadow-sm transition-all outline-none cursor-pointer';

  return (
    <div className="bg-white/20 backdrop-blur-3xl rounded-2xl sm:rounded-[34px] shadow-2xl max-w-6xl w-full mx-auto border border-white/30 animate-fade-in relative flex flex-col p-6 sm:p-10 max-h-[92vh] overflow-y-auto custom-scrollbar">
      
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6 pb-6 border-b border-white/20 gap-4">
        <div className="flex items-center gap-4">
          <button onClick={onBack} className="w-12 h-12 flex-none bg-white/60 hover:bg-white/80 text-slate-700 hover:text-red-500 flex items-center justify-center rounded-full transition-all shadow-sm border border-white/40 cursor-pointer backdrop-blur-sm shrink-0">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M15 19l-7-7 7-7"></path></svg>
          </button>
          <div>
            <h1 className="text-xl sm:text-3xl font-black text-slate-800 tracking-tight leading-tight">Programa Agua Potable</h1>
            <p className="text-xs sm:text-sm font-bold text-slate-600 mt-1">Control progresivo por semana y mantenimientos (FT-CD-08)</p>
          </div>
        </div>

        <button
          onClick={guardarRegistro}
          disabled={guardando}
          className="w-full sm:w-auto bg-emerald-600 hover:bg-emerald-500 disabled:opacity-60 text-white px-6 py-3 rounded-xl text-xs font-black shadow-md transition-colors cursor-pointer shrink-0 uppercase tracking-wider"
        >
          {guardando ? 'Guardando...' : `Guardar Semana ${semanaActiva}`}
        </button>
      </div>

      {mensaje.text && (
        <div className={`mb-6 p-4 rounded-xl text-xs font-bold flex justify-between items-center backdrop-blur-md border ${mensaje.type === 'success' ? 'bg-emerald-100 border-emerald-200 text-emerald-800' : mensaje.type === 'warning' ? 'bg-amber-100 border-amber-200 text-amber-800' : 'bg-red-100 border-red-200 text-red-800'}`}>
          <span>{mensaje.text}</span>
          <button onClick={() => setMensaje({ text: '', type: '' })} className="text-xs font-black cursor-pointer">✕</button>
        </div>
      )}

      <div className="bg-white/60 backdrop-blur-md rounded-3xl shadow-sm p-6 border border-white/80 space-y-6">
        
        <div className="bg-sky-50/80 p-5 rounded-2xl border border-sky-100/50 shadow-inner grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          
          <div className="flex flex-col space-y-1.5">
            <label className="text-[10px] font-black text-sky-800 uppercase tracking-wider">Mes y Año a Evaluar</label>
            <div className="flex items-center gap-2 h-10">
              <select className="w-2/3 h-full bg-white/90 border border-white rounded-xl px-3 text-xs font-bold text-slate-800 outline-none shadow-sm cursor-pointer" value={mesSeleccionado} onChange={e => setMesSeleccionado(e.target.value)}>
                <option value="01">Enero</option><option value="02">Febrero</option><option value="03">Marzo</option>
                <option value="04">Abril</option><option value="05">Mayo</option><option value="06">Junio</option>
                <option value="07">Julio</option><option value="08">Agosto</option><option value="09">Septiembre</option>
                <option value="10">Octubre</option><option value="11">Noviembre</option><option value="12">Diciembre</option>
              </select>
              <input type="number" className="w-1/3 h-full bg-white/90 border border-white rounded-xl px-2 text-xs font-bold text-slate-800 outline-none text-center shadow-sm" value={anioSeleccionado} onChange={e => setAnioSeleccionado(e.target.value)} />
            </div>
          </div>

          <div className="flex flex-col space-y-1.5">
            <label className="text-[10px] font-black text-sky-800 uppercase tracking-wider">Sede a Evaluar *</label>
            <div className="relative w-full h-10">
              <select name="pdv" value={formData.pdv} onChange={e => setFormData({ ...formData, pdv: e.target.value })} className={`${inputClass} appearance-none pr-8 absolute inset-0`} required>
                <option value="" disabled>Seleccione sede...</option>
                {sedesDisponibles.map(s => <option key={s.id} value={String(s.nombre || '').toUpperCase()}>{s.nombre}</option>)}
              </select>
              <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-sky-600 text-[10px] font-black">▼</div>
            </div>
          </div>

          <div className="flex flex-col space-y-1.5">
            <label className="text-[10px] font-black text-sky-800 uppercase tracking-wider">Inspector / Evaluador</label>
            <input type="text" value={formData.inspector} readOnly className={`${inputClass} bg-sky-100/50 text-sky-900 cursor-not-allowed`} />
          </div>

          <div className="flex flex-col space-y-1.5">
            <label className="text-[10px] font-black text-sky-800 uppercase tracking-wider">Punto de Muestreo</label>
            <input type="text" className={inputClass} value={formData.muestra_tomada_de} onChange={e => setFormData({ ...formData, muestra_tomada_de: e.target.value })} placeholder="Ej: Tanque, Filtro, Llave..." />
          </div>
          
        </div>

        <div>
          <div className="flex flex-wrap justify-between items-center mb-3 gap-2">
            <div>
              <h3 className="text-sm font-black text-slate-800 uppercase tracking-wider">Control de Calidad (Diario)</h3>
              <p className="text-[11px] font-bold text-slate-500">
                Puedes cambiar entre Semana 1 y Semana 5 para consultar o completar días anteriores del mes.
              </p>
            </div>
            <div className="flex items-center gap-2">
              {cargandoPlanilla && (
                <span className="text-[11px] font-bold text-sky-600 animate-pulse">Sincronizando semanas del mes...</span>
              )}
              <span className="text-xs font-extrabold text-sky-700 bg-sky-100 px-3 py-1 rounded-full border border-sky-200 shadow-sm">
                Viendo: Semana {semanaActiva} de 5
              </span>
            </div>
          </div>

          <div className="grid grid-cols-5 gap-2 mb-4">
            {[1, 2, 3, 4, 5].map((sem) => {
              const conDatos = semanaTieneDatos(sem);
              return (
                <button
                  key={sem}
                  type="button"
                  onClick={() => setSemanaActiva(sem)}
                  className={`py-2.5 px-2 text-xs font-black rounded-xl border transition-all cursor-pointer shadow-sm flex items-center justify-center gap-1.5 ${
                    semanaActiva === sem
                      ? 'bg-sky-600 text-white border-sky-700 shadow-md scale-[1.02]'
                      : conDatos
                      ? 'bg-emerald-50/90 text-emerald-800 border-emerald-300 hover:bg-emerald-100'
                      : 'bg-white/70 text-slate-700 border-white hover:bg-white'
                  }`}
                >
                  <span>S{sem} <span className="hidden sm:inline">Semana {sem}</span></span>
                  {conDatos && (
                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-black ${semanaActiva === sem ? 'bg-white/25 text-white' : 'bg-emerald-200 text-emerald-900'}`} title="Semana con registros guardados">
                      ✓
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          <div className="overflow-x-auto border border-white/80 rounded-2xl shadow-sm bg-white/40 backdrop-blur-md">
            <div className="bg-slate-800 text-white p-3 font-black text-center text-[11px] tracking-widest uppercase flex justify-between px-4">
              <span>Semana {semanaActiva} {semanaTieneDatos(semanaActiva) ? '• (Con registros guardados)' : '• (Sin registros aún)'}</span>
              <span className="font-normal opacity-70">C = Cumple | NC = No Cumple</span>
            </div>
            <table className="w-full text-xs text-center border-collapse">
              <thead>
                <tr className="bg-sky-100/50 border-b border-sky-200/50">
                  <th className="p-3 border-r border-sky-100 text-left w-48 text-sky-900 font-black pl-4">Aspecto Evaluado</th>
                  {DIAS_SEMANA.map(dia => <th key={dia} className="p-3 border-r border-sky-100 text-sky-900 font-black">{dia}</th>)}
                </tr>
              </thead>
              <tbody>
                {['sabor', 'olor', 'color', 'materia'].map(campo => (
                  <tr key={campo} className="border-b border-white/60 hover:bg-white/50 transition-colors">
                    <td className="p-2.5 border-r border-white/60 font-black text-left uppercase text-slate-700 pl-4">{campo.replace('materia', 'Materia Extraña')}</td>
                    {DIAS_SEMANA.map(dia => (
                      <td key={dia} className="p-2 border-r border-white/60">
                        <select
                          className={`w-full border border-gray-200 rounded-lg p-1.5 text-center font-black shadow-inner cursor-pointer outline-none focus:border-sky-400 ${
                            semanaActualData.dias[dia]?.[campo] === 'C'
                              ? 'bg-emerald-50 text-emerald-700 border-emerald-300'
                              : semanaActualData.dias[dia]?.[campo] === 'NC'
                              ? 'bg-red-50 text-red-700 border-red-300'
                              : 'bg-white/90 text-slate-800'
                          }`}
                          value={semanaActualData.dias[dia]?.[campo] || ''}
                          onChange={e => handleMedicionChange(dia, campo, e.target.value)}
                        >
                          <option value="">-</option>
                          <option value="C">C</option>
                          <option value="NC">NC</option>
                        </select>
                      </td>
                    ))}
                  </tr>
                ))}
                
                <tr className="border-b border-white/60 bg-sky-50/30">
                  <td className="p-2.5 border-r border-white/60 font-black text-left text-blue-700 pl-4">Cloro Residual (mg/L)</td>
                  {DIAS_SEMANA.map(dia => (
                    <td key={dia} className="p-2 border-r border-white/60">
                      <input
                        type="text"
                        className="w-full bg-white/90 border border-gray-200 rounded-lg p-1.5 text-center font-bold text-blue-700 shadow-inner outline-none focus:border-blue-400"
                        placeholder="0.3-2.0"
                        value={semanaActualData.dias[dia]?.cloroDato || ''}
                        onChange={e => handleMedicionChange(dia, 'cloroDato', e.target.value)}
                      />
                    </td>
                  ))}
                </tr>
                <tr className="border-b border-white/60 bg-sky-50/30">
                  <td className="p-2.5 border-r border-white/60 font-black text-left text-slate-700 pl-4">Cloro (C / NC)</td>
                  {DIAS_SEMANA.map(dia => (
                    <td key={dia} className="p-2 border-r border-white/60">
                      <select
                        className={`w-full border border-gray-200 rounded-lg p-1.5 text-center font-black shadow-inner cursor-pointer outline-none ${
                          semanaActualData.dias[dia]?.cloro === 'C'
                            ? 'bg-emerald-50 text-emerald-700 border-emerald-300'
                            : semanaActualData.dias[dia]?.cloro === 'NC'
                            ? 'bg-red-50 text-red-700 border-red-300'
                            : 'bg-white/90 text-slate-800'
                        }`}
                        value={semanaActualData.dias[dia]?.cloro || ''}
                        onChange={e => handleMedicionChange(dia, 'cloro', e.target.value)}
                      >
                        <option value="">-</option>
                        <option value="C">C</option>
                        <option value="NC">NC</option>
                      </select>
                    </td>
                  ))}
                </tr>

                <tr className="border-b border-white/60 bg-emerald-50/30">
                  <td className="p-2.5 border-r border-white/60 font-black text-left text-emerald-700 pl-4">PH (Dato)</td>
                  {DIAS_SEMANA.map(dia => (
                    <td key={dia} className="p-2 border-r border-white/60">
                      <input
                        type="text"
                        className="w-full bg-white/90 border border-gray-200 rounded-lg p-1.5 text-center font-bold text-emerald-700 shadow-inner outline-none focus:border-emerald-400"
                        placeholder="6.5-9.0"
                        value={semanaActualData.dias[dia]?.phDato || ''}
                        onChange={e => handleMedicionChange(dia, 'phDato', e.target.value)}
                      />
                    </td>
                  ))}
                </tr>
                <tr className="bg-emerald-50/30">
                  <td className="p-2.5 border-r border-white/60 font-black text-left text-slate-700 pl-4">PH (C / NC)</td>
                  {DIAS_SEMANA.map(dia => (
                    <td key={dia} className="p-2 border-r border-white/60">
                      <select
                        className={`w-full border border-gray-200 rounded-lg p-1.5 text-center font-black shadow-inner cursor-pointer outline-none ${
                          semanaActualData.dias[dia]?.ph === 'C'
                            ? 'bg-emerald-50 text-emerald-700 border-emerald-300'
                            : semanaActualData.dias[dia]?.ph === 'NC'
                            ? 'bg-red-50 text-red-700 border-red-300'
                            : 'bg-white/90 text-slate-800'
                        }`}
                        value={semanaActualData.dias[dia]?.ph || ''}
                        onChange={e => handleMedicionChange(dia, 'ph', e.target.value)}
                      >
                        <option value="">-</option>
                        <option value="C">C</option>
                        <option value="NC">NC</option>
                      </select>
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div className="pt-4">
          <div className="flex justify-between items-center border-b border-slate-200 pb-3 mb-4">
            <h3 className="text-sm font-black text-slate-800 uppercase tracking-wider">Registro de Mantenimientos</h3>
            <button type="button" onClick={agregarMantenimiento} className="bg-sky-100 hover:bg-sky-200 text-sky-700 px-4 py-2 rounded-xl text-[10px] font-black shadow-sm transition-all cursor-pointer border border-sky-200 uppercase tracking-wider">+ Nuevo Registro</button>
          </div>

          {mantenimientos.length === 0 ? (
            <div className="bg-white/40 border border-dashed border-sky-200 rounded-2xl p-6 text-center shadow-inner">
              <p className="text-xs font-bold text-slate-500">No se han registrado mantenimientos o lavados en este mes.</p>
            </div>
          ) : (
            <div className="overflow-x-auto rounded-2xl border border-white/80 shadow-sm bg-white/40 backdrop-blur-md">
              <table className="w-full text-xs border-collapse text-left">
                <thead className="bg-slate-800 text-white font-black uppercase text-[10px] tracking-wider">
                  <tr>
                    <th className="p-3 border-r border-slate-700">Elemento intervenido</th>
                    <th className="p-3 border-r border-slate-700 w-40">Fecha Ejecución</th>
                    <th className="p-3 border-r border-slate-700">Descripción de la labor</th>
                    <th className="p-3 border-r border-slate-700">Observaciones</th>
                    <th className="p-3 w-16 text-center">Acción</th>
                  </tr>
                </thead>
                <tbody>
                  {mantenimientos.map((mant, idx) => (
                    <tr key={idx} className="border-b border-white/60 hover:bg-white/50 transition-colors">
                      <td className="p-2 border-r border-white/60"><input type="text" className="w-full bg-white/90 border border-gray-200 rounded-lg p-2 text-xs font-bold text-slate-800 outline-none shadow-inner focus:border-sky-400" placeholder="Ej: Tanque Principal" value={mant.elemento} onChange={e => { const nm = [...mantenimientos]; nm[idx].elemento = e.target.value; setMantenimientos(nm); }}/></td>
                      <td className="p-2 border-r border-white/60"><input type="date" className="w-full bg-white/90 border border-gray-200 rounded-lg p-2 text-xs font-bold text-slate-800 outline-none shadow-inner focus:border-sky-400" value={mant.fecha} onChange={e => { const nm = [...mantenimientos]; nm[idx].fecha = e.target.value; setMantenimientos(nm); }}/></td>
                      <td className="p-2 border-r border-white/60"><input type="text" className="w-full bg-white/90 border border-gray-200 rounded-lg p-2 text-xs font-bold text-slate-800 outline-none shadow-inner focus:border-sky-400" placeholder="Lavado general..." value={mant.descripcion} onChange={e => { const nm = [...mantenimientos]; nm[idx].descripcion = e.target.value; setMantenimientos(nm); }}/></td>
                      <td className="p-2 border-r border-white/60"><input type="text" className="w-full bg-white/90 border border-gray-200 rounded-lg p-2 text-xs font-bold text-slate-800 outline-none shadow-sm focus:border-sky-400" placeholder="Sin novedades..." value={mant.observaciones} onChange={e => { const nm = [...mantenimientos]; nm[idx].observaciones = e.target.value; setMantenimientos(nm); }}/></td>
                      <td className="p-2 text-center">
                        <button type="button" onClick={() => setMantenimientos(mantenimientos.filter((_, i) => i !== idx))} className="bg-red-50 text-red-500 hover:bg-red-500 hover:text-white w-8 h-8 rounded-lg font-black transition-colors cursor-pointer border border-red-200 shadow-sm">✕</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="flex justify-end pt-6 border-t border-slate-200/50 mt-4">
          <button
            onClick={guardarRegistro}
            disabled={guardando}
            className="w-full sm:w-auto bg-[#007aff] hover:bg-[#005bb5] disabled:opacity-60 text-white px-10 py-3.5 rounded-xl text-xs font-black shadow-lg transition-all cursor-pointer uppercase tracking-widest hover:-translate-y-0.5"
          >
            {guardando ? 'Guardando...' : '💾 Guardar Avance del Mes'}
          </button>
        </div>

      </div>
    </div>
  );
}